import React, { useEffect, useState } from "react";
import { IAnalysedProperty } from "../../../../../interface/addAnalyser";
import { useAnalysedPropertyContext } from "../AnalysedPropertyContext";
import { IPropertyMarketFactor } from "../../../../../interface/addAnalyser";
import InputSlider from "../../../../../components/ui/slider";
import { REIInputNumber } from "../../../../../components/ui";
import { ANALYSER_PROPERTY_TYPES, PROPERTY_TYPE } from "../../../../../utils/constants";
import { formatNumberWithDecimal, returnFormattedData } from "../../../../../utils/commonUtil";

interface IMarketFectors {
  setIsValid: (isValid: boolean) => void;
  updateProperty: (updated: Partial<IAnalysedProperty>) => void;
}

const MarketFectors: React.FC<IMarketFectors> = ({ setIsValid, updateProperty }) => {
  const { analysedProperty, userProfile } = useAnalysedPropertyContext();
  const [marketFectors, setMarketFectors] = useState<IPropertyMarketFactor | undefined>(undefined);
  const [reset, setReset] = useState<boolean>(false);

  useEffect(() => {
    if (analysedProperty && !reset) {
      setMarketFectors(analysedProperty.TaxMarket.PropertyMarketFactor);
      setReset(true);
    }
  }, [analysedProperty]);

  useEffect(() => {
    if (marketFectors) {
      setIsValid(true);
      updateProperty({
        ...analysedProperty,
        TaxMarket: {
          ...analysedProperty?.TaxMarket,
          PropertyDepreciationEstimation: analysedProperty?.TaxMarket.PropertyDepreciationEstimation,
          PropertyMarketFactor: marketFectors,
          PropertyTaxRate: analysedProperty?.TaxMarket.PropertyTaxRate,
        },
      });
    }
  }, [marketFectors]);

  useEffect(() => {
    if (
      analysedProperty?.PropertyDetail.PropertyDetailId === 0 &&
      analysedProperty?.PropertyDetail.useDefault == "1" &&
      userProfile
    ) {
      setMarketFectors({
        ConsumerPriceIndex: userProfile.TaxRateMarketFactors.ConsumerPrice || 0,
        CapitalGrowth: userProfile.TaxRateMarketFactors.CapitalGrowth || 0,
      });
    }
  }, [analysedProperty?.PropertyDetail.useDefault, userProfile]);

  const setValues = (name: string, value: string | number) => {
    setMarketFectors((prevState) => {
      if (!prevState) return undefined;
      const updatedExpense = {
        ...prevState,
        [name]: value !== undefined ? value : 0,
      };
      return updatedExpense;
    });
  };

  return (
    <div>
      <div className="relative rounded-lg pl-3 pr-1 py-3 border-solid border border-gray-300 bg-white mt-2">
        <div className="default-profile-header-container">
          <h3 className="text-[var(--primary-color)] mt-0 m-1 text-lg">Market Factors</h3>
        </div>
        <div className="p-4">
          <div className="flex flex-wrap mb-4">
            <div className="w-full md:w-1/2 p-2">
              <div className="flex items-center justify-evenly">
                <label className="w-1/3 text-left" htmlFor="Rates">
                  Consumer price index (%)
                </label>
                <InputSlider
                  id="mktf-sldr-consumerpriceindex"
                  value={
                    marketFectors?.ConsumerPriceIndex !== undefined
                      ? marketFectors.ConsumerPriceIndex > 10
                        ? 10
                        : marketFectors.ConsumerPriceIndex
                      : 0
                  }
                  onChange={(e) => setValues("ConsumerPriceIndex", Array.isArray(e.value) ? e.value[0] : e.value)}
                  classNames="w-1/2 mx-2"
                  min={0}
                  max={10}
                  step={0.01}
                />
                <REIInputNumber
                  id="mktf-num-consumerpriceindex"
                  value={marketFectors?.ConsumerPriceIndex}
                  onValueChange={(e) => setValues("ConsumerPriceIndex", e.target.value)}
                  useGrouping={true}
                  maxFractionDigits={2}
                  min={0}
                  max={100}
                />
              </div>
            </div>
            <div className="w-full md:w-1/2 p-2" title="Add estimated annual cost.">
              <div className="flex items-center justify-evenly">
                <label className="w-1/3 text-left" htmlFor="BodyCorp">
                  Capital growth (%)
                </label>
                <InputSlider
                  id="mktf-sldr-capitalgrowth"
                  value={
                    marketFectors?.CapitalGrowth !== undefined
                      ? marketFectors.CapitalGrowth > 10
                        ? 10
                        : marketFectors.CapitalGrowth
                      : 0
                  }
                  onChange={(e) => setValues("CapitalGrowth", Array.isArray(e.value) ? e.value[0] : e.value)}
                  step={0.01}
                  classNames="w-1/2 mx-2"
                  min={0}
                  max={10}
                />
                <REIInputNumber
                  id="mktf-num-capitalgrowth"
                  value={marketFectors?.CapitalGrowth}
                  onValueChange={(e) => setValues("CapitalGrowth", e.target.value)}
                  useGrouping={true}
                  maxFractionDigits={2}
                  min={0}
                  max={100}
                />
              </div>
            </div>
          </div>
          {analysedProperty?.PropertyDetail.Growth.StatsPostcode != null && (
            <div className="flex flex-wrap mb-4">
              <div className="w-full p-2">
                <label className="w-1/3">
                  {`* Historical capital growth for a `}
                  <>
                    {PROPERTY_TYPE.find((type) => type.type == analysedProperty?.PropertyDetail.Growth.PropertyType)
                      ?.type ? (
                      <b>
                        {
                          PROPERTY_TYPE.find(
                            (type) => type.type == analysedProperty?.PropertyDetail.Growth.PropertyType
                          )?.type
                        }
                      </b>
                    ) : (
                      "House"
                    )}
                  </>
                  {` in `}
                  <b>{analysedProperty?.PropertyDetail.Growth.StatsSuburb}</b>
                  {` is `}
                  <b>
                    {returnFormattedData(
                      Number(
                        formatNumberWithDecimal(
                          analysedProperty?.PropertyDetail.Growth.StatsSuburbLongTermGrowth * 100,
                          1,
                          1
                        )
                      ),
                      "",
                      "%"
                    )}
                  </b>
                  {` within the `}
                  <b>{analysedProperty?.PropertyDetail.Growth.StatsRegion}</b>
                  {` region which has historical annual property growth for a `}
                  <b>
                    {PROPERTY_TYPE.find((type) => type.type == analysedProperty?.PropertyDetail.Growth.PropertyType)
                      ?.type ? (
                      <>
                        {
                          PROPERTY_TYPE.find(
                            (type) => type.type == analysedProperty?.PropertyDetail.Growth.PropertyType
                          )?.type
                        }
                      </>
                    ) : (
                      "House"
                    )}
                  </b>
                  {` of `}
                  <b>
                    {returnFormattedData(
                      Number(
                        formatNumberWithDecimal(
                          analysedProperty?.PropertyDetail.Growth.StatsRegionLongTermGrowth * 100,
                          1,
                          1
                        )
                      ),
                      "",
                      "%"
                    )}
                  </b>
                  {`.`}
                </label>
              </div>
            </div>
          )}
          {analysedProperty?.PropertyDetail.CountryId === 1 && (
            <div className="flex flex-wrap mb-4">
              <div className="w-full md:w-1/2 p-2">
                <div className="flex items-center justify-evenly">
                  <label className="w-1/3 text-left" htmlFor="Rates">
                    Land tax ($)
                  </label>
                  <InputSlider
                    id="mktf-sldr-landtax"
                    value={
                      marketFectors?.Expense_LandTax !== undefined
                        ? marketFectors.Expense_LandTax > 20000
                          ? 20000
                          : marketFectors.Expense_LandTax
                        : 0
                    }
                    onChange={(e) => setValues("Expense_LandTax", Array.isArray(e.value) ? e.value[0] : e.value)}
                    classNames="w-1/2 mx-2"
                    min={0}
                    max={20000}
                    step={1}
                  />
                  <REIInputNumber
                    id="mktf-num-landtax"
                    value={marketFectors?.Expense_LandTax}
                    onValueChange={(e) => setValues("Expense_LandTax", e.target.value)}
                    useGrouping={true}
                    min={0}
                  />
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default MarketFectors;
